import type { SharedVolume } from "@/lib/api";
import { IconButton } from "@/components/common/IconButton";
import { useHasPermission } from "@/lib/permissions";
import {
	ResourceDetail,
	ResourceDetails,
	ResourceRow,
	ResourceStatus,
	ResourceTitle,
} from "@/components/common/ResourceCard";
import type { StatusDotTone } from "@/components/common/StatusDot";
import {
	SHARED_VOLUME_STATE_LABELS,
	formatSharedVolumeMeasuredAt,
	formatSharedVolumeUsage,
	isSharedVolumeTransitional,
	sharedVolumeDeleteDisabledReason,
} from "@/lib/shared-volumes";

const STATE_TONE: Record<SharedVolume["state"], StatusDotTone> = {
	ready: "success",
	creating: "warning",
	deleting: "warning",
	error: "danger",
};

/** 共有ボリューム 1 件の行。状態・マウント先・使用容量 (概算)・マウント先環境を示す。 */
export function SharedVolumeRow({
	volume,
	onDelete,
}: {
	volume: SharedVolume;
	onDelete: () => void;
}) {
	const canManageSharedVolumes = useHasPermission("manageSharedVolumes");
	const deleteDisabledReason = sharedVolumeDeleteDisabledReason(volume);
	return (
		<ResourceRow
			testId={`shared-volume-row-${volume.id}`}
			compact
			title={<ResourceTitle compact>{volume.name}</ResourceTitle>}
			tags={
				<ResourceStatus
					tone={STATE_TONE[volume.state]}
					indicator={isSharedVolumeTransitional(volume.state) ? "pulse" : "dot"}
					testId={`shared-volume-state-${volume.id}`}
				>
					{SHARED_VOLUME_STATE_LABELS[volume.state]}
				</ResourceStatus>
			}
			meta={
				<span
					style={{
						fontFamily: "var(--font-mono)",
						color: "var(--fg-2)",
						overflowWrap: "anywhere",
					}}
				>
					{volume.mountPath}
				</span>
			}
			actions={
				canManageSharedVolumes && (
					<IconButton
						icon="trash"
						size={28}
						hoverColor="var(--danger)"
						data-tooltip={deleteDisabledReason ?? "共有ボリュームを削除 (データは失われます)"}
						data-testid={`shared-volume-delete-${volume.id}`}
						disabled={deleteDisabledReason !== null}
						onClick={onDelete}
					/>
				)
			}
		>
			{volume.state === "error" && (
				<span style={{ paddingTop: 4, fontSize: "var(--text-caption)", color: "var(--danger)" }}>
					準備に失敗しました。削除して作り直してください。
				</span>
			)}
			<ResourceDetails>
				<ResourceDetail label="使用容量 (概算)">
					<span style={{ color: "var(--fg-2)" }}>{formatSharedVolumeUsage(volume)}</span>
				</ResourceDetail>
				<ResourceDetail label="最終計測">
					<span style={{ fontVariantNumeric: "tabular-nums", color: "var(--fg-2)" }}>
						{formatSharedVolumeMeasuredAt(volume)}
					</span>
				</ResourceDetail>
				<ResourceDetail label="マウント先のエージェント">
					<span
						data-testid={`shared-volume-attached-${volume.id}`}
						style={{ color: "var(--fg-2)" }}
					>
						{volume.attachedEnvironments.length === 0
							? "なし"
							: volume.attachedEnvironments.map((e) => e.name).join("、")}
					</span>
				</ResourceDetail>
			</ResourceDetails>
		</ResourceRow>
	);
}
